import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const yugoslavLanguages = [
    { code: 'bs', label: 'Bosanski', flag: '🇧🇦' },
    { code: 'hr', label: 'Hrvatski', flag: '🇭🇷' },
    { code: 'sr', label: 'Srpski', flag: '🇷🇸' },
    // Future languages to add:
    // { code: 'sl', label: 'Slovenščina', flag: '🇸🇮' },
    // { code: 'mk', label: 'Македонски', flag: '🇲🇰' },
    // { code: 'sq', label: 'Shqip', flag: '🇽🇰' },
  ];

  const isYugoslavLanguage = yugoslavLanguages.some(lang => lang.code === i18n.language);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setShowDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  return (
    <div className="flex items-center gap-2">
      {/* English Flag Button */}
      <button
        onClick={() => changeLanguage('en')}
        className={`flex items-center justify-center border-2 transition-all overflow-hidden bg-white ${
          i18n.language === 'en'
            ? 'border-black'
            : 'border-black hover:bg-gray-100'
        }`}
        title="English"
      >
        {/* British Flag SVG */}
        <svg width="32" height="22" viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg">
          <rect width="60" height="30" fill="#012169" />
          <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
          <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="4" />
          <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
          <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
        </svg>
      </button>

      {/* Yugoslav Flag Button with Dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className={`flex items-center justify-center gap-1 pl-0 pr-1 border-2 transition-all overflow-hidden bg-white ${
            isYugoslavLanguage
              ? 'border-black'
              : 'border-black hover:bg-gray-100'
          }`}
          title="Yugoslav Languages"
        >
          {/* Yugoslav Flag SVG (Blue-White-Red with Red Star) */}
          <svg width="32" height="22" viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg">
            <rect width="60" height="10" y="0" fill="#0C47B7" />
            <rect width="60" height="10" y="10" fill="#FFFFFF" />
            <rect width="60" height="10" y="20" fill="#C6363C" />
            <path
              d="M30,6 L32,12 L38,12 L33,16 L35,22 L30,18 L25,22 L27,16 L22,12 L28,12 Z"
              fill="#C6363C"
              stroke="#FFD700"
              strokeWidth="0.5"
            />
          </svg>
          <svg
            className={`w-3 h-3 transition-transform ${showDropdown ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown Menu */}
        {showDropdown && (
          <div className="absolute top-full mt-2 right-0 z-50 bg-white border-3 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] min-w-[200px]">
            {yugoslavLanguages.map(lang => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left font-bold text-sm transition-colors border-b-2 border-black last:border-b-0 ${
                  i18n.language === lang.code
                    ? 'bg-[#C41E3A] text-white'
                    : 'bg-white text-black hover:bg-gray-100'
                }`}
              >
                <span className="text-xl">{lang.flag}</span>
                <span>{lang.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LanguageSwitcher;
